import { Bookmark } from "@novorender/data-js-api";
import * as htmlToImage from "html-to-image";

export function applyGroupStatusToBookmark(bm: Bookmark, groupId: string, status: "hidden" | "frozen"): Bookmark {
    const { explorerState } = bm;
    if (!explorerState?.groups) {
        return bm;
    }
    const { groups } = explorerState;
    const existing = groups.find((g) => g.id === groupId);
    if (existing && existing.status !== status) {
        return {
            ...bm,
            explorerState: {
                ...explorerState,
                groups: groups.map((g) => (g.id === groupId ? { ...g, status } : g)),
            },
        };
    }
    if (!existing) {
        return {
            ...bm,
            explorerState: {
                ...explorerState,
                groups: [...groups, { id: groupId, status }],
            },
        };
    }
    return bm;
}

export const ELEMENT_IDS = Object.freeze({
    renderContainer: "render-container",
    mainCanvas: "main-canvas",
    markupContainer: "markup-container",
    markersContainer: "markers-container",
});

export async function createBookmarkImg(canvas: HTMLCanvasElement): Promise<string> {
    const width = canvas.width;
    const height = canvas.height;
    let dx = 0;
    let dy = 0;

    if (height / width < 0.7) {
        dx = width - Math.round((height * 10) / 7);
    } else {
        dy = height - Math.round(width * 0.7);
    }

    const dist = document.createElement("canvas");
    dist.height = 70;
    dist.width = 100;
    const ctx = dist.getContext("2d", { alpha: true, desynchronized: false })!;

    ctx.drawImage(
        canvas,
        Math.round(dx / 2),
        Math.round(dy / 2),
        width - dx,
        height - dy,
        0,
        0,
        dist.width,
        dist.height,
    );

    return dist.toDataURL("image/jpeg");
}

type CreateSnapshotImgInnerOptions = {
    width?: number;
    height?: number;
    type?: "jpeg" | "png";
};

async function createSnapshotImgInner(options: CreateSnapshotImgInnerOptions = {}) {
    // Scrape the render container for keepIds
    const keepIds = new Set<string>([ELEMENT_IDS.mainCanvas, ELEMENT_IDS.markupContainer]);
    const renderContainerEl = document.getElementById(ELEMENT_IDS.renderContainer);
    if (!renderContainerEl) {
        throw new Error(`#${ELEMENT_IDS.renderContainer} not found`);
    }

    // Multiply by devicePixelRatio to get physical pixel dimensions on higher
    // DPI screens.
    const width = renderContainerEl.clientWidth * window.devicePixelRatio;
    const height = renderContainerEl.clientHeight * window.devicePixelRatio;

    const newCanvas = await htmlToImage.toCanvas(renderContainerEl, {
        filter: (node: HTMLElement) => keepIds.has(node.id),
        skipFonts: true,
    });

    // Determine dimensions of new image
    const { sx, sy, sw, sh, targetWidth, targetHeight } = positionSnapshot(
        width,
        height,
        options.width,
        options.height,
    );

    // Draw dimensions from intermediate canvas to final canvas
    const dist = document.createElement("canvas");
    dist.width = targetWidth;
    dist.height = targetHeight;
    const distCtx = dist.getContext("2d", { alpha: true, desynchronized: false })!;
    distCtx.drawImage(newCanvas, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);

    return dist;
}

export async function createSnapshotImgAsArrayBuffer(
    options: CreateSnapshotImgInnerOptions = {},
): Promise<ArrayBuffer> {
    const dist = await createSnapshotImgInner(options);
    const blob = await new Promise<Blob | null>((resolve) => {
        dist.toBlob(resolve, `image/${options.type || "jpeg"}`);
    });
    if (!blob) {
        throw new Error("Failed to create snapshot blob");
    }
    return await blob.arrayBuffer();
}

export async function createSnapshotImg(options: CreateSnapshotImgInnerOptions = {}) {
    const dist = await createSnapshotImgInner(options);
    return dist.toDataURL(`image/${options.type || "jpeg"}`);
}

// exported for testing
export function positionSnapshot(
    width: number,
    height: number,
    requestedWidth: number | undefined,
    requestedHeight: number | undefined,
) {
    const aspectRatio = width / height;

    let targetWidth = 0;
    let targetHeight = 0;

    if (requestedWidth && requestedHeight) {
        targetWidth = requestedWidth;
        targetHeight = requestedHeight;
    } else if (requestedWidth) {
        targetWidth = requestedWidth;
        targetHeight = Math.round(requestedWidth / aspectRatio);
    } else if (requestedHeight) {
        targetWidth = Math.round(requestedHeight * aspectRatio);
        targetHeight = requestedHeight;
    } else {
        targetWidth = width;
        targetHeight = height;
    }

    let dx = 0;
    let dy = 0;

    if (targetWidth <= 0 || targetHeight <= 0) {
        throw new Error(`Invalid target dimensions: targetWidth=${targetWidth}, targetHeight=${targetHeight}`);
    }
    const targetAspectRatio = targetWidth / targetHeight;

    if (Math.abs(aspectRatio - targetAspectRatio) >= 0.01) {
        if (aspectRatio < targetAspectRatio) {
            dy = height - Math.round(height * (aspectRatio / targetAspectRatio));
        } else {
            dx = width - Math.round(width * (targetAspectRatio / aspectRatio));
        }
    }

    return {
        sx: Math.round(dx / 2),
        sy: Math.round(dy / 2),
        sw: width - dx,
        sh: height - dy,
        targetWidth,
        targetHeight,
    };
}
